/**
 * drunk-mode — čistá logika „opileckého" rozvlnění obsahu (SVG heat-distortion).
 *
 * Config žije v `content_pages` (klíč `drunk-mode`, JSON), fetchuje se při bootu.
 * Vlastní efekt aplikuje `DrunkModeController` (třída na <body> + scale SVG filtru).
 *
 * Režimy:
 *   - off            — vypnuto (default)
 *   - optIn          — admin povolil; každý uživatel si zapne sám (localStorage)
 *   - byBeers        — intenzita roste s počtem vypitých piv přihlášeného hráče
 *   - byTeamLosing   — intenzita roste s tím, jak moc tým hráče prohrává (v pivech)
 *   - byTeamWinning  — intenzita roste s tím, jak moc tým hráče vyhrává (v pivech)
 */

export type DrunkMode = "off" | "optIn" | "byBeers" | "byTeamLosing" | "byTeamWinning";

/** Týmová barva se samostatným součtem piv (vip nemá týmový součet). */
export type DrunkTeamColor = "blue" | "green" | "gold";

/** Součet vypitých piv per tým (z veřejného scoreboardu `teamTotals`). */
export interface TeamBeerTotals {
  blue: number;
  green: number;
  gold: number;
}

export interface DrunkConfig {
  mode: DrunkMode;
  /** Strop intenzity 0..1 (1 = plné vlnění). */
  maxIntensity: number;
}

export const DEFAULT_DRUNK_CONFIG: DrunkConfig = { mode: "off", maxIntensity: 0.7 };

/** Počet piv, při kterém je vlnění (v režimu byBeers) na maximu. */
export const DRUNK_BEERS_FULL = 10;

/** Maximální posun feDisplacementMap (px) při intenzitě 1. */
export const DRUNK_MAX_SCALE = 26;

/** localStorage klíč per-user opt-in přepínače. */
export const DRUNK_OPTIN_KEY = "bw:drunk";

/** Custom event — DrunkToggle ho vyšle při změně opt-in, controller přepočítá. */
export const DRUNK_CHANGED_EVENT = "bw:drunk-changed";

function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(1, n));
}

/** Bezpečně rozparsuje JSON z content_pages na DrunkConfig (s defaulty). */
export function parseDrunkConfig(json: string): DrunkConfig {
  try {
    const raw = JSON.parse(json) as Partial<DrunkConfig>;
    const KNOWN: DrunkMode[] = ["off", "optIn", "byBeers", "byTeamLosing", "byTeamWinning"];
    const mode: DrunkMode = KNOWN.includes(raw.mode as DrunkMode) ? (raw.mode as DrunkMode) : "off";
    const maxIntensity =
      typeof raw.maxIntensity === "number" ? clamp01(raw.maxIntensity) : DEFAULT_DRUNK_CONFIG.maxIntensity;
    return { mode, maxIntensity };
  } catch {
    return DEFAULT_DRUNK_CONFIG;
  }
}

export interface DrunkInputs {
  config: DrunkConfig;
  /** Per-user opt-in zapnut (localStorage). Relevantní jen v režimu optIn. */
  optInEnabled: boolean;
  /** Počet vypitých piv přihlášeného hráče v aktivní sezóně. null = nepřihlášen / neznámo. */
  consumedBeers: number | null;
  /** Týmová barva přihlášeného hráče v aktivní sezóně (jen team režimy). null = nepřihlášen / bez týmu. */
  teamColor?: DrunkTeamColor | null;
  /** Součty piv per tým z veřejného scoreboardu (jen team režimy). null = neznámo. */
  teamBeers?: TeamBeerTotals | null;
  /** OS preference — když true, efekt se úplně vypne. */
  reducedMotion: boolean;
  /** Role přihlášeného uživatele (z localStorage). null = nepřihlášen. */
  role?: "viewer" | "player" | "bartender" | "admin" | null;
  /** Role uzlu serveru (z /healthz). null = neznámo. */
  nodeRole?: "local" | "public" | null;
  /** Klient přišel z téže mašiny jako server — barový kiosk (z /healthz). */
  localTerminal?: boolean;
}

export interface DrunkState {
  active: boolean;
  /** 0..1 — síla vlnění (controller mapuje na scale filtru). */
  intensity: number;
}

const OFF: DrunkState = { active: false, intensity: 0 };

/**
 * Obsluha baru (admin/barman) na local terminálu (kiosk = táž mašina jako server)
 * se nesmí rozvlnit — efekt by jen překážel při ovládání. Týká se jen local uzlu a
 * klienta z loopbacku / vlastní IP serveru. Platí pro všechny „herní" režimy.
 */
function isBarKiosk(i: DrunkInputs): boolean {
  return (
    i.localTerminal === true &&
    i.nodeRole === "local" &&
    (i.role === "admin" || i.role === "bartender")
  );
}

/**
 * Síla efektu 0..1 pro týmové režimy. Tým hráče musí být blue/green/gold.
 *   - "losing":  jak moc tým hráče ZAOSTÁVÁ za vedoucím týmem = `(vedoucí − můj) / vedoucí`.
 *     Vedoucí tým → 0 (klid); tým s nulou piv vůči vedoucímu → 1 (max).
 *   - "winning": jak moc tým hráče VEDE nad nejlepším z ostatních = `(můj − druhý) / můj`.
 *     Tým, který nevede → 0; čím větší náskok vůči vlastnímu součtu, tím blíž 1.
 */
export function teamDrunkFraction(
  variant: "losing" | "winning",
  teamColor: DrunkTeamColor | null | undefined,
  beers: TeamBeerTotals | null | undefined,
): number {
  if (!beers) return 0;
  if (teamColor !== "blue" && teamColor !== "green" && teamColor !== "gold") return 0;
  const order: DrunkTeamColor[] = ["blue", "green", "gold"];
  const mine = beers[teamColor];
  const all = order.map((c) => beers[c]);
  const others = order.filter((c) => c !== teamColor).map((c) => beers[c]);

  if (variant === "losing") {
    const leader = Math.max(...all);
    if (leader <= 0) return 0;
    return clamp01((leader - mine) / leader); // 0 když jsem vedoucí
  }
  // winning
  if (mine <= 0) return 0;
  const othersMax = others.length ? Math.max(...others) : 0;
  return clamp01(Math.max(0, mine - othersMax) / mine); // 0 když nevedu
}

/** Vyhodnotí, zda a jak silně rozvlnit — čistá funkce (testovatelná). */
export function resolveDrunkState(i: DrunkInputs): DrunkState {
  if (i.reducedMotion) return OFF;
  const max = clamp01(i.config.maxIntensity);
  if (max <= 0) return OFF;

  switch (i.config.mode) {
    case "optIn":
      return i.optInEnabled ? { active: true, intensity: max } : OFF;
    case "byBeers": {
      if (isBarKiosk(i)) return OFF;
      const beers = i.consumedBeers ?? 0;
      if (beers <= 0) return OFF;
      const intensity = clamp01(beers / DRUNK_BEERS_FULL) * max;
      return intensity > 0 ? { active: true, intensity } : OFF;
    }
    case "byTeamLosing":
    case "byTeamWinning": {
      if (isBarKiosk(i)) return OFF;
      const variant = i.config.mode === "byTeamLosing" ? "losing" : "winning";
      const intensity = teamDrunkFraction(variant, i.teamColor, i.teamBeers) * max;
      return intensity > 0 ? { active: true, intensity } : OFF;
    }
    case "off":
    default:
      return OFF;
  }
}

/** Intenzita 0..1 → posun feDisplacementMap v px. */
export function intensityToScale(intensity: number): number {
  return Math.round(clamp01(intensity) * DRUNK_MAX_SCALE);
}

// ─── Config store (vanilla) — sdílený mezi controllerem a toggle/admin ─────────

let _config: DrunkConfig = DEFAULT_DRUNK_CONFIG;
const _subs = new Set<() => void>();

export function getDrunkConfig(): DrunkConfig {
  return _config;
}

export function setDrunkConfig(c: DrunkConfig): void {
  _config = c;
  _subs.forEach((f) => f());
}

export function subscribeDrunkConfig(cb: () => void): () => void {
  _subs.add(cb);
  return () => {
    _subs.delete(cb);
  };
}

/** Načte config z content_pages (`drunk-mode`). 404 / chyba → ponechá default (off). */
export async function loadDrunkConfig(): Promise<void> {
  try {
    const res = await fetch("/api/content/drunk-mode", { headers: { Accept: "application/json" } });
    if (!res.ok) return;
    const data = (await res.json()) as { html?: string };
    if (data.html) setDrunkConfig(parseDrunkConfig(data.html));
  } catch {
    // ponech default
  }
}
